/**
 * tileScope -- the "what do these numbers count?" label on summary tiles (OP#1787).
 *
 * THE BUG THIS EXISTS FOR. Summary tiles (Health KPI row, the Devices status buckets) showed
 * bare numbers. Whether "32 devices" meant one customer, one building, a building manager's own
 * sites, every customer on the platform, or only the rows matching a search was implicit, and
 * #1599 showed how that goes wrong: an msp_admin read platform-wide totals as one customer's.
 * #1599 fixed the COUNTS; nothing told the operator which scope they were reading.
 *
 * THE RULE: every tile carries one short scope line, built here so all tiles word it the same:
 *   - "All customers"                      platform_admin, no customer selected
 *   - "3 customers"                        an msp_admin's own customers, none selected
 *   - "<Customer> · All sites"             one customer, every site
 *   - "<Customer> · <Site>"                one site
 *   - "<Customer> · Your 2 sites"          a site-confined role (read_only / site_operator)
 *                                          with no site selected: their own buildings only
 *   - "… · Filtered: type Switch, search "ab12""   the list filters the counts honour
 * plus `excluded`: list filters the counts do NOT apply (said out loud rather than letting the
 * tile and the table below silently disagree).
 *
 * The Health tiles take the scope the SERVER reports it counted (`scope` on
 * /dashboard/overview, api/v2/dashboard.tile_scope), so the label cannot drift from the counts.
 * Names come from the caller (session /me), which already applies the display rules
 * (quarantine org is never shown by its stored name).
 *
 * Pure (no React, no store, no "@/" runtime imports) so `npm run test:unit` pins it directly.
 */

/** What the server says a set of tile counts covers (api/v2/dashboard.tile_scope). */
export interface TileScope {
  kind: "all_customers" | "customers" | "customer" | "site" | "sites";
  org_ids: Array<number | string> | null;
  site_id: number | string | null;
  site_ids: Array<number | string> | null;
}

/** One list filter as the operator sees it: "type" -> "Switch". */
export interface ScopeFilter {
  label: string;
  value: string;
}

/** Name lookups supplied by the caller (from /me), already display-ruled. */
export interface ScopeNames {
  org: (id: string) => string | null | undefined;
  site: (id: string) => string | null | undefined;
}

export interface ScopeLabel {
  /** The visible line, e.g. "Acme · Building A · Filtered: search \"ab12\"". */
  text: string;
  /** Longer sentence for the tooltip / accessible description. */
  title: string;
  /** True when list filters narrow the counts (the label shows them). */
  filtered: boolean;
}

/** Separator between scope parts. A middle dot reads as "and", not as a path. */
export const SEP = " · ";

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function orgPart(scope: TileScope, names: ScopeNames): string {
  const ids = (scope.org_ids ?? []).map(String);
  if (scope.kind === "all_customers" || scope.org_ids === null) return "All customers";
  if (ids.length === 1) return names.org(ids[0]!) || `Customer ${ids[0]}`;
  return plural(ids.length, "customer", "customers");
}

function filterText(filters: ScopeFilter[]): string {
  return filters.map((f) => (f.label === "search" ? `search "${f.value}"` : `${f.label} ${f.value}`)).join(", ");
}

/**
 * Build the scope line for a tile row.
 *
 * @param scope     what the counts cover (server-reported for Health; built by the caller from
 *                  the very params it sent for the Devices buckets)
 * @param names     id -> display name lookups
 * @param filters   list filters the counts DO honour (shown as "Filtered: …")
 * @param excluded  list filters the counts do NOT honour (named in the title)
 */
export function tileScopeLabel(
  scope: TileScope | null | undefined,
  names: ScopeNames,
  filters: ScopeFilter[] = [],
  excluded: string[] = [],
): ScopeLabel | null {
  if (!scope) return null;
  const parts: string[] = [orgPart(scope, names)];
  let sentence: string;
  if (scope.kind === "site" && scope.site_id != null) {
    const site = names.site(String(scope.site_id)) || `Site ${scope.site_id}`;
    parts.push(site);
    sentence = `Counts cover one site: ${site}.`;
  } else if (scope.kind === "sites") {
    const n = (scope.site_ids ?? []).length;
    parts.push(n === 0 ? "No sites assigned" : `Your ${plural(n, "site", "sites")}`);
    sentence = n === 0
      ? "Counts cover nothing: no sites are assigned to your account."
      : `Counts cover only the ${plural(n, "site", "sites")} assigned to you, not the whole customer.`;
  } else if (scope.kind === "customer") {
    parts.push("All sites");
    sentence = `Counts cover every site of ${parts[0]}.`;
  } else if (scope.kind === "customers") {
    sentence = `Counts cover every site of your ${parts[0]}.`;
  } else {
    sentence = "Counts cover every customer on the platform.";
  }
  const active = filters.filter((f) => f.value !== "");
  if (active.length > 0) {
    parts.push(`Filtered: ${filterText(active)}`);
    sentence += ` Narrowed by ${filterText(active)}.`;
  }
  if (excluded.length > 0) {
    sentence += ` Not applied to these counts: ${excluded.join(", ")}.`;
  }
  return { text: parts.join(SEP), title: sentence, filtered: active.length > 0 };
}

/**
 * The scope the Devices status buckets count, built from the SAME values the page sends to
 * GET /devices/status-counts (so label and counts cannot disagree).
 *
 * @param orgFilter   effective customer filter ("" = none -> all customers in reach)
 * @param siteFilter  the site the counts were asked for ("" = none, "unassigned" = no site)
 * @param confinedSiteIds  a site-confined caller's own sites (null for org-wide roles)
 * @param reachOrgIds the caller's orgs when no customer is pinned (null = platform-wide)
 */
export function deviceBucketScope(
  orgFilter: string,
  siteFilter: string,
  confinedSiteIds: string[] | null,
  reachOrgIds: string[] | null,
): TileScope {
  if (siteFilter && siteFilter !== "unassigned") {
    return { kind: "site", org_ids: orgFilter ? [orgFilter] : reachOrgIds, site_id: siteFilter, site_ids: null };
  }
  const orgIds = orgFilter ? [orgFilter] : reachOrgIds;
  if (confinedSiteIds) {
    return { kind: "sites", org_ids: orgIds, site_id: null, site_ids: confinedSiteIds };
  }
  if (orgIds === null) return { kind: "all_customers", org_ids: null, site_id: null, site_ids: null };
  return {
    kind: orgIds.length === 1 ? "customer" : "customers",
    org_ids: orgIds,
    site_id: null,
    site_ids: null,
  };
}

/** The /me fields the confinement rule reads (structural, so tests need no full Me). */
export interface MeLike {
  orgs: Array<{ id: string }>;
  sites: Array<{ id: string; org_id: string }>;
  roles: Array<{ role: string; org_id: string; site_id: string | null }>;
  is_msp_admin: boolean;
  is_platform_admin?: boolean;
}

/** Roles that see a whole org (security/authz: org_admin and above). Below this every role is
 *  SITE-CONFINED server-side (api/v2/_helpers.scoped_site_ids), whatever its grant says. */
const ORG_WIDE_ROLES = new Set(["org_admin", "msp_admin", "platform_admin"]);

/**
 * The sites a caller's counts are confined to inside `orgId`, or null when the caller sees
 * the whole org (mirror of the server's scoped_site_ids, used for the label only -- the server
 * enforces the real rule). platform_admin / msp_admin and any org_admin+ grant in the org are
 * org-wide; otherwise the caller's site grants in that org (falling back to /me sites).
 */
export function confinedSiteIds(me: MeLike | null | undefined, orgId: string | null): string[] | null {
  if (!me || me.is_platform_admin === true || me.is_msp_admin) return null;
  const inOrg = me.roles.filter((g) => orgId == null || String(g.org_id) === String(orgId));
  if (inOrg.some((g) => ORG_WIDE_ROLES.has(g.role))) return null;
  const granted = inOrg.filter((g) => g.site_id != null).map((g) => String(g.site_id));
  if (granted.length > 0) return Array.from(new Set(granted)).sort();
  return me.sites.filter((s) => orgId == null || String(s.org_id) === String(orgId)).map((s) => String(s.id)).sort();
}

/** The orgs a caller reaches when no customer is pinned: null = platform-wide (platform_admin). */
export function reachOrgIds(me: MeLike | null | undefined): string[] | null {
  if (!me || me.is_platform_admin === true) return null;
  return me.orgs.map((o) => String(o.id));
}
